import { PARTY_SIZE } from "odyssey-shared";
import type { PartyNoticeKind, PartyRefusal } from "odyssey-shared";
import { say } from "../../words.ts";
import { muteTime, waitSeconds } from "../chat/words.ts";
import { PAGE_RULES } from "../rules.ts";
import { sameName, type PartyName } from "./members.ts";

/**
 * The words of the party's notices and refusals (ADR-101 decisions 4, 5, 9 and 19, and its note of 2026-10-15), each a
 * system line in the chat log.
 */

/**
 * The request a refusal answers, as far as the page remembers it: the gateway's `partyRefused` names a reason and a
 * wait but not the request, so the page keeps the last one it sent and the name that request carried.
 */
export interface RefusalContext {
  readonly request: "invite" | "answer" | "leave" | "remove" | "promote" | "grant" | null;
  readonly name: string | null;
}

/**
 * The reasons this build words, as a set, so a reason from a later server is told apart from the closed set's.
 */
const KNOWN: ReadonlySet<string> = new Set<PartyRefusal>(["rate", "recent", "busy", "muted", "leader", "cannot", "self", "member", "full", "pending", "combat", "absent", "offline", "alone", "gone", "unavailable", "flag", "level"]);

/**
 * The sentence a player reads for a party request the gateway refused. A reason the page does not know gets one
 * sentence of its own.
 *
 * @param reason The refusal's reason, as sent.
 * @param waitMs The wait the refusal carried, for a limit.
 * @param context The request it answers.
 * @returns The sentence.
 * @example
 * partyRefusalSentence("cannot", 0, { request: "invite", name: "Kaelen Ashgrove" });
 * // "Kaelen Ashgrove cannot be invited."
 */
export function partyRefusalSentence(reason: string, waitMs: number, context: RefusalContext): string {
  if (!KNOWN.has(reason)) {
    return say("party.refused.unknown");
  }
  const name = context.name;
  switch (reason as PartyRefusal) {
    case "rate":
      return say("party.refused.rate", { seconds: String(waitSeconds(waitMs)) });
    case "recent":
      return say("party.refused.recent", { time: muteTime(waitMs) });
    case "busy":
      return say("party.refused.busy", { time: muteTime(waitMs) });
    case "muted":
      return say("party.refused.muted");
    case "leader":
      return say(context.request === "invite" ? "party.refused.leaderInvite" : "party.refused.leaderOnly");
    case "cannot":
      return context.request === "answer" ? say("party.refused.cannotJoin") : name === null ? say("party.refused.cannotAnyone") : say("party.refused.cannot", { name });
    case "self":
      return say("party.refused.self");
    case "member":
      return name === null ? say("party.refused.memberAnyone") : say("party.refused.member", { name });
    case "full":
      return say("party.refused.full", { max: String(PARTY_SIZE) });
    case "pending":
      return say("party.refused.pending");
    case "combat":
      return context.request === "answer" ? say("party.refused.combatJoin") : say("party.refused.combat");
    case "absent":
      return say("party.refused.absent");
    case "offline":
      return say("party.refused.offline");
    case "alone":
      return say("party.refused.alone");
    case "gone":
      return say("party.refused.gone");
    case "unavailable":
      return say("party.refused.unavailable");
    case "flag":
      return say("party.refused.flag");
    case "level":
      return say("party.refused.level", { level: String(PAGE_RULES.constants.pvp_level) });
  }
}

/**
 * The system line a party notice makes. A notice about the player itself is worded in the second person.
 *
 * @param kind The notice's kind, as sent.
 * @param about The character the notice names.
 * @param self The player's own names, or null before they are known.
 * @returns The line, or null for a kind this build does not know, which draws nothing.
 * @example
 * partyNoticeLine("joined", { firstName: "Mira", familyName: "Thornwood" }, null);
 * // "Mira Thornwood joined the party."
 */
export function partyNoticeLine(kind: string, about: PartyName, self: PartyName | null): string | null {
  const name = `${about.firstName} ${about.familyName}`.trim();
  const mine = self !== null && sameName(about, self);
  switch (kind as PartyNoticeKind) {
    case "joined":
      return mine ? say("party.notice.joinedSelf") : say("party.notice.joined", { name });
    case "left":
      return mine ? say("party.notice.leftSelf") : say("party.notice.left", { name });
    case "removed":
      return mine ? say("party.notice.removedSelf") : say("party.notice.removed", { name });
    case "leader":
      return mine ? say("party.notice.leaderSelf") : say("party.notice.leader", { name });
    case "granted":
      return mine ? say("party.notice.grantedSelf") : say("party.notice.granted", { name });
    case "revoked":
      return mine ? say("party.notice.revokedSelf") : say("party.notice.revoked", { name });
    case "declined":
      return say("party.notice.declined", { name });
    case "expired":
      return say("party.notice.expired", { name });
    case "withdrawn":
      return say("party.notice.withdrawn", { name });
    case "offline":
      return say("party.notice.offline", { name });
    case "online":
      return say("party.notice.online", { name });
    case "ended":
      return say("party.notice.ended");
    case "flag":
      return mine ? say("party.notice.flagMismatch") : null;
    default:
      return null;
  }
}
